import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  useWindowDimensions,
  Animated,
  Platform,
} from 'react-native';
import FontFamily from '../../../../../constants/fonts';
import SparcKpiCard from '../../../components/SparcKpiCard';
import LighthouseKpiCard from '../../../components/LighthouseKpiCard';
import LighthouseTask from '../../../components/LighthouseTask';
import NewsCard from '../../../components/NewsCard';
import DotPagination from '../../../components/DotPagination';
import { PrintIcon } from '../../StandupIcons';
import SparcKpiPrintOptionsModal from '../../Print/SparcKpiPrintOptionsModal';
import ReviewProgressKpiPrint, {
  ReviewProgressKpiPrintRef,
  SparcKpiPrintCard as ReviewSparcKpiPrintCard,
  LighthouseKpiPrintCard,
} from '../../Print/ReviewProgressKpiPrint';
import ReviewProgressTasksPrint, {
  ReviewProgressTasksPrintRef,
  ReviewProgressTaskPrintItem,
} from '../../Print/ReviewProgressTasksPrint';
import {
  KPI_CATEGORY_COLORS,
  KPI_CATEGORY_FILTER_ORDER,
  resolveKpiCategory,
} from '../../../../AnalyticsHub/utils/kpiHelpers';
import { NewsItem } from '../../../types';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../store';

// Loading Skeleton Component
const SparcKpiCardSkeleton: React.FC = () => {
  const opacity = React.useRef(new Animated.Value(0.3)).current;

  React.useEffect(() => {
    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, {
          toValue: 1,
          duration: 800,
          useNativeDriver: true,
        }),
        Animated.timing(opacity, {
          toValue: 0.3,
          duration: 800,
          useNativeDriver: true,
        }),
      ])
    );
    animation.start();
    return () => animation.stop();
  }, [opacity]);

  return (
    <View style={styles.skeletonCard}>
      {/* Header Skeleton */}
      <View style={styles.skeletonHeader}>
        <View style={styles.skeletonHeaderLeft}>
          <Animated.View style={[styles.skeletonBadge, { opacity }]} />
          <Animated.View style={[styles.skeletonTitle, { opacity }]} />
        </View>
        <Animated.View style={[styles.skeletonFavorite, { opacity }]} />
      </View>

      {/* Metrics Skeleton */}
      <View style={styles.skeletonMetrics}>
        {[1, 2, 3].map((_, index) => (
          <View key={index} style={styles.skeletonMetricItem}>
            <Animated.View style={[styles.skeletonMetricLabel, { opacity }]} />
            <Animated.View style={[styles.skeletonMetricValue, { opacity }]} />
          </View>
        ))}
      </View>

      {/* Chart Skeleton */}
      <View style={styles.skeletonChart}>
        <View style={styles.skeletonYAxis}>
          <Animated.View style={[styles.skeletonYLabel, { opacity }]} />
          <Animated.View style={[styles.skeletonYLabel, { opacity }]} />
        </View>
        <View style={styles.skeletonBars}>
          {[1, 2, 3, 4, 5, 6, 7].map((_, index) => (
            <Animated.View
              key={index}
              style={[
                styles.skeletonBar,
                { height: 20 + Math.random() * 40, opacity },
              ]}
            />
          ))}
        </View>
      </View>
    </View>
  );
};

interface ReviewProgressTabProps {
  favoriteSparcCards: string[];
  favoriteLighthouseCards: string[];
  favoriteLighthouseTasks: string[];
  allTasks: any[];
  allKpiCards: any[];
  standupNewsList: NewsItem[]; // News associated with this standup (for Selected News section)
  currentNewsPage: number;
  onNewsPageChange: (page: number) => void;
  onDeleteNews: (newsId: string) => void;
  onOpenNewsFeedOverlay: () => void;
  onViewMore?: (newsId: string) => void;
  onToggleFavorite?: (newsId: number) => void;
  togglingNewsId?: number | null;
  isViewMode?: boolean;
  kfaFavorites?: any[];
  kpiFavorites?: any[];
  lighthouseMetricFavorites?: any[];
  onLighthouseCardActionPlan?: (card: any) => void;
}

export const ReviewProgressTab: React.FC<ReviewProgressTabProps> = ({
  favoriteSparcCards,
  favoriteLighthouseCards,
  favoriteLighthouseTasks,
  allTasks,
  allKpiCards,
  standupNewsList,
  currentNewsPage,
  onNewsPageChange,
  onDeleteNews,
  onOpenNewsFeedOverlay,
  onViewMore,
  onToggleFavorite,
  togglingNewsId,
  isViewMode = false,
  kfaFavorites = [],
  kpiFavorites = [],
  lighthouseMetricFavorites = [],
  onLighthouseCardActionPlan,
}) => {
  const { width: windowWidth } = useWindowDimensions();
  const reviewKpiPrintRef = React.useRef<ReviewProgressKpiPrintRef | null>(null);
  const reviewTasksPrintRef = React.useRef<ReviewProgressTasksPrintRef | null>(null);
  const isWeb = Platform.OS === 'web';
  const [showKpiPrintOptionsModal, setShowKpiPrintOptionsModal] = React.useState(false);

  // Calculate responsive card width based on screen size - matching Lighthouse Execution style
  // Start with 4 cards per row, then increase to 5, 6+ as screen gets wider
  const getCardWidth = () => {
    if (windowWidth >= 1600) return 'calc(20% - 10px)'; // 5 columns for very wide
    if (windowWidth >= 1900) return 'calc(16.666% - 10px)'; // 6 columns for ultra-wide
    if (windowWidth >= 1400) return 'calc(25% - 9px)'; // 4 columns (default wide)
    if (windowWidth >= 1024) return 'calc(33.333% - 8px)'; // 3 columns
    if (windowWidth >= 768) return 'calc(50% - 6px)'; // 2 columns
    return '100%'; // 1 column for mobile
  };

  const getMinWidth = () => {
    if (windowWidth >= 1400) return 250;
    if (windowWidth >= 768) return 0;
    return 0;
  };

  // Selected News: use standupNewsList directly
  // (standupNewsList already contains standup-specific news and starred global news that got associated)
  const selectedNews = standupNewsList;

  // Paginate selected news - 4 per page
  const newsPerPage = 4;
  const totalNewsPages = Math.ceil(selectedNews.length / newsPerPage);
  const paginatedNews = selectedNews.slice(
    currentNewsPage * newsPerPage,
    (currentNewsPage + 1) * newsPerPage
  );

  console.log('=== ReviewProgressTab: Received allKpiCards ===', allKpiCards);
  console.log('=== ReviewProgressTab: isViewMode ===', isViewMode);

  // Get selected KPIs from Redux
  const selectedKpiFromRedux = useSelector((state: RootState) => state.standupRedirect.reviewProgress.selectedKpi);

  // Compute selected KPI cards
  const selectedKpiCards = React.useMemo(() => {
    if (isViewMode) {
      // In view mode, use the favorites hook data
      const viewModeCards = [...(kpiFavorites || []), ...(lighthouseMetricFavorites || [])];

      // Backfill isMandatory for SPARC cards using allKpiCards data when not present in favorites payload.
      return viewModeCards.map((card: any) => {
        if (card?.type !== 'sparc') {
          return card;
        }

        if (typeof card?.isMandatory === 'boolean') {
          return card;
        }

        const matchingCard = (allKpiCards || []).find((kpiCard: any) => {
          if (kpiCard?.type !== 'sparc') return false;
          const idMatch = String(kpiCard?.id ?? '') === String(card?.id ?? '');
          const titleMatch = String(kpiCard?.title ?? '').toLowerCase() === String(card?.title ?? '').toLowerCase();
          return idMatch || titleMatch;
        });

        return {
          ...card,
          isMandatory: Boolean(matchingCard?.isMandatory),
        };
      });
    }

    // In edit/new mode, use the selected KPIs from Redux (set by SparcKpiListSection)
    console.log('ReviewProgressTab - Selected KPIs from Redux:', selectedKpiFromRedux);
    return (selectedKpiFromRedux || []).map((card: any) => {
      if (card?.type !== 'sparc') {
        return card;
      }

      if (typeof card?.isMandatory === 'boolean') {
        return card;
      }

      const matchingCard = (allKpiCards || []).find((kpiCard: any) => {
        if (kpiCard?.type !== 'sparc') return false;
        const idMatch = String(kpiCard?.id ?? '') === String(card?.id ?? '');
        const titleMatch = String(kpiCard?.title ?? '').toLowerCase() === String(card?.title ?? '').toLowerCase();
        return idMatch || titleMatch;
      });

      return {
        ...card,
        isMandatory: Boolean(matchingCard?.isMandatory),
      };
    });
  }, [isViewMode, kpiFavorites, lighthouseMetricFavorites, selectedKpiFromRedux, allKpiCards]);

  const sparcCardsToPrint = React.useMemo<ReviewSparcKpiPrintCard[]>(
    () =>
      (selectedKpiCards || [])
        .filter((card: any) => card?.type === 'sparc')
        .map((card: any) => ({
          id: String(card?.id ?? card?.title ?? ''),
          title: String(card?.title ?? ''),
          badgeLetter: String(card?.badgeLetter ?? ''),
          badgeColor: String(card?.badgeColor ?? '#D4F4DD'),
          actual: Number(card?.actual ?? 0),
          target: Number(card?.target ?? 0),
          delta: Number(card?.delta ?? 0),
          chartData: Array.isArray(card?.chartData) ? card.chartData : [],
        })),
    [selectedKpiCards],
  );

  const lighthouseCardsToPrint = React.useMemo<LighthouseKpiPrintCard[]>(
    () =>
      (selectedKpiCards || [])
        .filter((card: any) => card?.type === 'lighthouse')
        .map((card: any) => ({
          id: String(card?.id ?? card?.title ?? ''),
          title: String(card?.title ?? ''),
          cw: card?.cw ?? 0,
          lcw: card?.lcw ?? 0,
          lcp: card?.lcp ?? 0,
          ytd: card?.ytd ?? 0,
          chartData: Array.isArray(card?.chartData) ? card.chartData : [],
        })),
    [selectedKpiCards],
  );

  const groupedSelectedKpis = React.useMemo(() => {
    const sparcGroups = KPI_CATEGORY_FILTER_ORDER
      .map((category) => ({
        key: category,
        label: category.toUpperCase(),
        color: KPI_CATEGORY_COLORS[category].icon,
        items: selectedKpiCards.filter((card: any) => {
          if (card?.type !== 'sparc') return false;
          const resolvedCategory = resolveKpiCategory(card?.category || card?.badgeLetter || '');
          return resolvedCategory === category;
        }),
      }))
      .filter((group) => group.items.length > 0);

    const lighthouseItems = selectedKpiCards.filter((card: any) => card?.type === 'lighthouse');
    const lighthouseGroup = lighthouseItems.length > 0
      ? [{
          key: 'lighthouse-execution',
          label: 'LIGHTHOUSE EXECUTION',
          color: '#0B5FC6',
          items: lighthouseItems,
        }]
      : [];

    return [...sparcGroups, ...lighthouseGroup];
  }, [selectedKpiCards]);

  console.log('ReviewProgressTab - selectedKpiCards:', selectedKpiCards);

  const { ciDashboardData } = useSelector((state: RootState) => state.homescreen);

  // Get loading states from Redux
  const kpiMetricsLoading = useSelector((state: RootState) => state.standupRedirect.reviewPerformance.kpiMetrics.loading);
  const lighthouseExecutionLoading = useSelector((state: RootState) => state.standupRedirect.reviewPerformance.lighthouseExecution.loading);
  const isKpiDataLoading = kpiMetricsLoading || lighthouseExecutionLoading;

  // Compute selected tasks - include both matched and unmatched favorites
  // This mirrors the logic in KeyActivitiesTab to ensure consistency
  const selectedTasks = React.useMemo(() => {
    // In view mode, use favorites from API (kfaFavorites) instead of Redux
    if (isViewMode) {
      return kfaFavorites && kfaFavorites.length > 0 ? kfaFavorites : [];
    }

    // In edit/new mode, calculate selected tasks from kfaFavorites and allTasks
    // This includes unmatched favorites (same logic as KeyActivitiesTab)
    if (kfaFavorites.length === 0) {
      return [];
    }

    const result: any[] = [];
    const ciData = ciDashboardData?.data || [];

    kfaFavorites.forEach((favorite: any) => {
      const itemId = String(favorite.item_id || '');
      const itemName = favorite.item_name || '';
      const moduleName = favorite.module_name || '';

      // Check if this is a Continuous Improvement item
      const isContinuousImprovement = 
        moduleName.toLowerCase().includes('continuous improvement') ||
        moduleName.toLowerCase().includes('continuousimprovement') ||
        moduleName.toLowerCase() === 'continuous improvement';

      let matchedTask = null;

      if (isContinuousImprovement) {
        // Look for matching CI task
        matchedTask = allTasks.find((task: any) => {
          const taskCiId = String(task.ci_id || '');
          return taskCiId === itemId && taskCiId !== '' && taskCiId !== 'null';
        });

        if (matchedTask && matchedTask.isFavorite) {
          result.push(matchedTask);
        }
      } else {
        // Look for matching lighthouse task using improved matching logic
        matchedTask = allTasks.find((task: any) => {
          const taskKfaId = String(task.kfa_id || '');
          const taskModuleName = String(task.module_name || '');
          const taskKfaType = String(task.kfa_type || '');
          
          // Must match by kfa_id first
          const idMatch = taskKfaId === itemId;
          if (!idMatch) return false;

          // If we have module information in favorite, validate it matches
          if (moduleName) {
            let moduleMatch = false;
            
            if (moduleName.includes('_')) {
              // Format: "ModuleName_KfaType" - match both parts exactly
              const [expectedModuleName, expectedKfaType] = moduleName.split('_');
              moduleMatch = 
                taskModuleName.toLowerCase() === expectedModuleName.toLowerCase() &&
                taskKfaType.toLowerCase() === expectedKfaType.toLowerCase();
            } else {
              // Simple module name - match against module_name or kfa_type
              moduleMatch = 
                taskModuleName.toLowerCase() === moduleName.toLowerCase() ||
                taskKfaType.toLowerCase() === moduleName.toLowerCase();
            }
            
            return moduleMatch;
          }

          return true; // Fallback to ID match only
        });

        if (matchedTask && matchedTask.isFavorite) {
          result.push(matchedTask);
        }
      }

      // If no matching task found, create unmatched task from Redux favorite
      if (!matchedTask) {
        const unmatchedTask = {
          id: `unmatched_${favorite.favourite_id}`,
          kfa_id: favorite.item_id,
          title: favorite.module_name || 'Unknown Module',
          subHeader: '',
          description: favorite.item_name || '',
          module_name: favorite.module_name || '',
          category: isContinuousImprovement ? 'Continuous Improvement' : (favorite.module_name || 'Unknown'),
          isFavorite: true,
          isUnmatched: true,
          favourite_id: favorite.favourite_id,
        };
        result.push(unmatchedTask);
      }
    });

    return result;
  }, [isViewMode, kfaFavorites, allTasks, ciDashboardData]);

  const formatTaskSubHeader = (task: any): string => {
    let displaySubHeader = task?.subHeader || '';

    const isContinuousImprovement =
      task?.module_name?.toLowerCase().includes('continuous improvement') ||
      task?.category?.toLowerCase().includes('continuous improvement');

    if (isContinuousImprovement && task?.subHeader) {
      try {
        const date = new Date(task.subHeader);
        if (!isNaN(date.getTime())) {
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const year = date.getFullYear();
          displaySubHeader = `${month}/${day}/${year}`;
        }
      } catch {
        displaySubHeader = task.subHeader;
      }
    }

    return displaySubHeader;
  };

  const tasksToPrint = React.useMemo<ReviewProgressTaskPrintItem[]>(
    () =>
      (selectedTasks || []).map((task: any) => ({
        id: String(task?.id ?? task?.kfa_id ?? ''),
        title: String(task?.title ?? ''),
        subHeader: formatTaskSubHeader(task),
        description: String(task?.description ?? ''),
        status: task?.status ? String(task.status) : undefined,
        category: task?.category ? String(task.category) : undefined,
      })),
    [selectedTasks],
  );

  return (
    <View style={styles.reviewProgressContainer}>
      {isWeb && (
        <>
          <ReviewProgressKpiPrint
            ref={reviewKpiPrintRef}
            sparcCards={sparcCardsToPrint}
            lighthouseCards={lighthouseCardsToPrint}
          />
          <ReviewProgressTasksPrint ref={reviewTasksPrintRef} tasks={tasksToPrint} />
        </>
      )}
      <SparcKpiPrintOptionsModal
        visible={showKpiPrintOptionsModal}
        onClose={() => setShowKpiPrintOptionsModal(false)}
        onPrintAllKpis={() => {
          setShowKpiPrintOptionsModal(false);
          reviewKpiPrintRef.current?.print();
        }}
        onPrintOnePerPage={() => {
          setShowKpiPrintOptionsModal(false);
          reviewKpiPrintRef.current?.printOnePerPage();
        }}
      />
      {/* Selected KPIs Section */}
      <View style={styles.reviewProgressSection}>
        <View style={styles.reviewProgressHeader}>
          <Text style={styles.reviewProgressTitle}>Selected KPIs</Text>
          {isWeb && (
            <TouchableOpacity
              style={styles.printButtonContainer}
              onPress={() => setShowKpiPrintOptionsModal(true)}
            >
              <PrintIcon />
              <Text style={styles.printButtonText}>Print</Text>
            </TouchableOpacity>
          )}
        </View>
        <View style={styles.reviewProgressCardContent}>
          {isKpiDataLoading ? (
            <View style={styles.reviewProgressCardsRow}>
              {[1, 2, 3, 4].map((index) => (
                <View
                  key={`skeleton-kpi-${index}`}
                  style={[
                    styles.reviewProgressCardWrapper,
                    {
                      flexBasis: getCardWidth() as any,
                      maxWidth: getCardWidth() as any,
                      minWidth: getMinWidth(),
                    }
                  ]}
                >
                  <SparcKpiCardSkeleton />
                </View>
              ))}
            </View>
          ) : selectedKpiCards.length === 0 ? (
            <View style={styles.reviewProgressEmptyState}>
              <Text style={styles.reviewProgressEmptyText}>No KPIs selected</Text>
            </View>
          ) : (
            <>
              {groupedSelectedKpis.map((group) => (
                <View key={`review-progress-group-${group.key}`} style={styles.kpiGroupContainer}>
                  <View style={styles.kpiGroupHeader}>
                    <View style={[styles.kpiGroupDot, { backgroundColor: group.color }]} />
                    <Text style={styles.kpiGroupTitle}>{group.label}</Text>
                    <Text style={styles.kpiGroupBadge}>
                      {group.items.length} {group.items.length === 1 ? 'metric' : 'metrics'}
                    </Text>
                  </View>

                  <View style={styles.reviewProgressCardsRow}>
                    {group.items.map((card: any) => {
                      if (card?.type === 'sparc') {
                        return (
                          <View
                            key={`kpi-${group.key}-${card.id}`}
                            style={[
                              styles.reviewProgressCardWrapper,
                              {
                                flexBasis: getCardWidth() as any,
                                maxWidth: getCardWidth() as any,
                                minWidth: getMinWidth(),
                              }
                            ]}
                          >
                            <SparcKpiCard
                              id={card.id}
                              title={card.title}
                              badgeLetter={card.badgeLetter}
                              badgeColor={card.badgeColor}
                              isMandatory={Boolean(card.isMandatory)}
                              actual={card.actual}
                              target={card.target}
                              delta={card.delta}
                              chartData={card.chartData}
                              isFavorite={true}
                              onFavoritePress={() => { }}
                              disableFavorite={true}
                            />
                          </View>
                        );
                      }

                      return (
                        <View
                          key={`kpi-${group.key}-${card.id || card.title}`}
                          style={[
                            styles.reviewProgressCardWrapper,
                            {
                              flexBasis: getCardWidth() as any,
                              maxWidth: getCardWidth() as any,
                              minWidth: getMinWidth(),
                            }
                          ]}
                        >
                          <LighthouseKpiCard
                            title={card.title}
                            cw={card.cw}
                            lcw={card.lcw}
                            lcp={card.lcp}
                            ytd={card.ytd}
                            chartData={card.chartData}
                            isFavorite={true}
                            onFavoritePress={() => { }}
                            disableFavorite={true}
                            disableExpandTooltip={true}
                          />
                        </View>
                      );
                    })}
                  </View>
                </View>
              ))}
            </>
          )}
        </View>
      </View>

      {/* Selected Tasks Section */}
      <View style={styles.reviewProgressSection}>
        <View style={styles.reviewProgressHeader}>
          <Text style={styles.reviewProgressTitle}>Selected Tasks</Text>
          {isWeb && (
            <TouchableOpacity
              style={styles.printButtonContainer}
              onPress={() => reviewTasksPrintRef.current?.print()}
            >
              <PrintIcon />
              <Text style={styles.printButtonText}>Print</Text>
            </TouchableOpacity>
          )}
        </View>
        <View style={styles.reviewProgressCardContent}>
          {selectedTasks.length === 0 ? (
            <View style={styles.reviewProgressEmptyState}>
              <Text style={styles.reviewProgressEmptyText}>No tasks selected</Text>
            </View>
          ) : (
            <View style={styles.reviewProgressTasksRow}>
              {selectedTasks.map((task) => {
                // Determine what to show in subHeader:
                // - For Continuous Improvement tasks: format the date (subHeader contains target end date)
                // - For other lighthouse tasks: use subHeader directly (contains "Occurrences: X")
                const displaySubHeader = formatTaskSubHeader(task);
               
                return (
                  <View
                    key={`review-task-${task.id}`}
                    style={[
                      styles.reviewProgressTaskWrapper,
                      {
                        flexBasis: getCardWidth() as any,
                        maxWidth: getCardWidth() as any,
                        minWidth: getMinWidth(),
                      }
                    ]}
                  >
                    <LighthouseTask
                      title={task.title}
                      subHeader={displaySubHeader}
                      description={task.description}
                      status={task?.status}
                      isFavorite={task.isFavorite}
                      width="100%"
                      disableFavorite={true}
                    />
                  </View>
                )
              })}
            </View>
          )}
        </View>
      </View>

      {/* Selected News Section - matching NewsTab's Selected News style */}
      <View style={styles.reviewProgressSection}>
        <View style={styles.reviewProgressHeader}>
          <Text style={styles.reviewProgressTitle}>Selected News</Text>
        </View>
        <View style={styles.reviewProgressCardNews}>
          {selectedNews.length === 0 ? (
            <View style={styles.reviewProgressEmptyState}>
              <Text style={styles.reviewProgressEmptyText}>No news added</Text>
            </View>
          ) : (
            <>
              <View style={styles.newsCardsRow}>
                {paginatedNews.map((news) => {
                  const newsIdNum = parseInt(news.id) || 0;
                  return (
                    <NewsCard
                      key={news.id}
                      news_id={newsIdNum}
                      title={news.title}
                      description={news.description}
                      date={news.date}
                      is_favourite={true}
                      width="calc(25% - 9px)"
                      handleToggleFavorite={() => {}}
                      isLoading={false}
                      disabled={true}
                      is_truncated={news.is_truncated}
                      onViewMore={onViewMore ? () => onViewMore(news.id) : undefined}
                      datePosition="below-title"
                    />
                  );
                })}
              </View>
              {totalNewsPages > 1 && (
                <View style={styles.newsPaginationContainer}>
                  <DotPagination
                    totalDots={totalNewsPages}
                    activeDot={currentNewsPage}
                    onDotPress={onNewsPageChange}
                  />
                </View>
              )}
            </>
          )}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  reviewProgressContainer: {
    // gap: 24,
  },
  reviewProgressSection: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
    marginTop: 24,
  },
  reviewProgressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
  },
  reviewProgressTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1C1C1C',
    fontFamily: FontFamily.bold,
  },
  printButtonContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: '#D8E7FB',
    borderRadius: 4,
  },
  printButtonText: {
    fontSize: 14,
    fontWeight: '400',
    color: '#034895',
    fontFamily: FontFamily.regular,
  },
  reviewProgressCardContent: {
    backgroundColor: '#F5F9FF',
    borderRadius: 8,
    padding: 20,
  },
  reviewProgressCardNews: {
    borderRadius: 8,
  },
  reviewProgressEmptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
  },
  reviewProgressEmptyText: {
    fontSize: 14,
    fontWeight: '400',
    color: '#9E9E9E',
    fontFamily: FontFamily.regular,
  },
  reviewProgressCardsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 16,
  },
  reviewProgressCardWrapper: {
    flexBasis: 'calc(25% - 9px)' as any,
    minWidth: 0,
    maxWidth: 'calc(25% - 9px)' as any,
  },
  kpiGroupContainer: {
    marginTop: 8,
  },
  kpiGroupHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 10,
  },
  kpiGroupDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  kpiGroupTitle: {
    fontSize: 14,
    fontFamily: FontFamily.bold,
    color: '#2D2F33',
    letterSpacing: 0.3,
  },
  kpiGroupBadge: {
    fontSize: 12,
    fontFamily: FontFamily.medium,
    color: '#5F6670',
    marginLeft: 4,
  },
  reviewProgressTasksRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 16,
  },
  reviewProgressTaskWrapper: {
    flexBasis: 'calc(25% - 9px)' as any,
    minWidth: 0,
    maxWidth: 'calc(25% - 9px)' as any,
  },
  newsCardsRow: {
    flexDirection: 'row',
    gap: 12,
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
  },
  newsPaginationContainer: {
    alignItems: 'center',
    marginTop: 12,
  },
  // Skeleton Styles
  skeletonCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
    minHeight: 300,
  },
  skeletonHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 20,
  },
  skeletonHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  skeletonBadge: {
    width: 32,
    height: 32,
    borderRadius: 4,
    backgroundColor: '#E0E0E0',
  },
  skeletonTitle: {
    flex: 1,
    height: 16,
    borderRadius: 4,
    backgroundColor: '#E0E0E0',
    maxWidth: 150,
  },
  skeletonFavorite: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: '#E0E0E0',
  },
  skeletonMetrics: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  skeletonMetricItem: {
    flex: 1,
    alignItems: 'center',
  },
  skeletonMetricLabel: {
    width: 40,
    height: 12,
    borderRadius: 4,
    backgroundColor: '#E0E0E0',
    marginBottom: 8,
  },
  skeletonMetricValue: {
    width: 60,
    height: 20,
    borderRadius: 4,
    backgroundColor: '#E0E0E0',
  },
  skeletonChart: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    height: 100,
  },
  skeletonYAxis: {
    justifyContent: 'space-between',
    height: '100%',
    marginRight: 8,
    paddingVertical: 4,
  },
  skeletonYLabel: {
    width: 30,
    height: 10,
    borderRadius: 4,
    backgroundColor: '#E0E0E0',
  },
  skeletonBars: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    height: '100%',
    gap: 4,
  },
  skeletonBar: {
    flex: 1,
    borderRadius: 4,
    backgroundColor: '#E0E0E0',
    minWidth: 20,
  },
});
